/**
 * A bundle item's price inside its bundle.
 *
 * A per-item-discount bundle (`pricingType: ITEM_DISCOUNT`) prices each item on
 * its own: `POST /bundle/resolve-prices` returns, per component, the item's
 * price for the visit date (`unitPrice`) and its price inside the bundle after
 * that item's own discount (`finalUnitPrice`). Any other bundle carries no
 * `finalUnitPrice`, and the item shows no price of its own.
 *
 * Components are matched by `itemIndex` (the item's position in the bundle's
 * `items`), because a multi-visitor bundle can hold the same product twice with
 * different discounts; an older backend without `itemIndex` falls back to the
 * productId. Display only — the server prices the sale.
 *
 * Mirrored in the kiosk and web shop (`src/@core/utils/bundle-component-price.js`).
 */

import type { ResolvedBundleComponent } from '@/api/bundle.api';

export function findBundleComponent(
  components: ResolvedBundleComponent[] | undefined,
  productId: string,
  itemIndex: number,
): ResolvedBundleComponent | null {
  if (!components || components.length === 0) return null;
  const byIndex = components.find((c) => c.itemIndex === itemIndex);
  if (byIndex) return byIndex;
  return components.find((c) => c.productId === productId) ?? null;
}

/** `{ price, originalPrice }` for an item of a per-item-discount bundle, else null. */
export function bundleComponentPrice(
  components: ResolvedBundleComponent[] | undefined,
  productId: string,
  itemIndex: number,
): { price: number; originalPrice: number | null } | null {
  const comp = findBundleComponent(components, productId, itemIndex);
  if (!comp || comp.finalUnitPrice == null) return null;
  return {
    price: comp.finalUnitPrice,
    originalPrice: comp.unitPrice > comp.finalUnitPrice ? comp.unitPrice : null,
  };
}
